import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import BaseInnerWrapper from '../../../global/InnerWrapper';
import Heading from './Heading';
import P from '../../../global/typography/P';

import { swatches } from '../../../../styles/theme';
import { splitList } from './utils';

const BreakpointDesktop = '(max-width: 1190px)';
const BreakpointMobile = '(max-width: 950px)';

const ColumnOne = styled.div`
  counter-reset: noble-list;

  @media ${BreakpointMobile} {
    margin: 0;
  }
`;

const ColumnTwo = styled.div`
  align-self: center;
  background-image: radial-gradient(ellipse closest-side, #676767, transparent);
  margin: ${p => (p.margin ? p.margin : 0)};

  @media ${BreakpointMobile} {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }
`;

const Disclaimer = styled(P)`
  color: ${swatches.offwhite};
  font-size: 12px;
  margin: 28px 0 0 0;
  position: absolute;
  bottom: 10px;
  right: 2.4%;

  @media ${BreakpointDesktop} {
    width: auto;
  }

  @media ${BreakpointMobile} {
    display: none;
  }
`;

const Image = styled.img`
  min-width: 300px;
  max-width: 520px;
  width: auto;
`;

const Indicator = styled.div`
  counter-increment: noble-list;
  padding: 0 6px 0 0;

  &::before {
    background-color: ${swatches.primary};
    border-radius: 100%;

    display: inline-block;
    font-weight: 700;
    text-align: center;

    margin-top: ${props => !props.isNumbered && '4px'};
    width: ${props => (props.isNumbered ? '24px' : '16px')};
    height: ${props => (props.isNumbered ? '24px' : '16px')};

    content: ${props => (props.isNumbered ? 'counter(noble-list)' : "''")};
  }

  @media ${BreakpointMobile} {
    display: none;
  }
`;

const InnerWrapper = styled(BaseInnerWrapper)`
  display: ${p => p.display};
  display: flex;
  gap: 32px;

  @media ${BreakpointMobile} {
    display: block;
  }
`;

const List = styled.ol`
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 0;

  ${props =>
    props.compact &&
    `
    &:first-child {
      margin-bottom: 24px;
    }
  `}

  @media ${BreakpointDesktop} {
    width: auto;

    &:first-child {
      margin: 0 0 24px 0;
    }
  }
`;

const ListHeading = styled(P)`
  color: ${swatches.white};
  font-weight: 700;

  @media ${BreakpointMobile} {
    color: ${swatches.primary};
  }
`;

const ListItem = styled(P)`
  color: ${swatches.white};
  display: flex;
  margin: 0 0 48px 0;

  ${props => props.compact && 'margin-bottom: 24px;'}

  &:last-child {
    margin: 0;
  }

  @media ${BreakpointDesktop} {
    margin: 0 0 24px 0;
  }
`;

const ListWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  margin: 68px 0 0 0;

  ${props =>
    props.compact &&
    `
      flex-direction: column;

      &:first-child: {
        margin-bottom: 24px;
      }
    `};

  @media ${BreakpointDesktop} {
    margin: 48px 0 0 0;
    flex-direction: column;
  }

  @media ${BreakpointMobile} {
    margin: 24px 0 0 0;
  }
`;

const Paragraph = styled(P)`
  color: ${swatches.offwhite};
  margin-bottom: 24px;
`;

const RedHighlight = styled.span`
  color: ${swatches.primary};
`;

const Wrapper = styled.section`
  background-color: ${swatches.gray.dark};
  color: ${swatches.white};
  padding: 60px 0;
  position: relative;

  @media ${BreakpointMobile} {
    padding: 32px 0;
  }
`;

const ImageCard = ({
  heading,
  highlight,
  image,
  imageMargin,
  list,
  paragraphs,
  showDisclaimer,
  isNumbered
}) => (
  <Wrapper>
    <InnerWrapper display="block">
      <Heading>
        {heading} <RedHighlight>{highlight}</RedHighlight>
      </Heading>
    </InnerWrapper>
    <InnerWrapper display="flex">
      <ColumnOne>
        {paragraphs?.map((paragraph, i) => (
          <Paragraph key={i}>{paragraph}</Paragraph>
        ))}
        <ListWrapper compact>
          {list &&
            splitList(list).map((subList, i) => (
              <List key={i} compact>
                {subList.map((li, j) => (
                  <ListItem key={j} as="li" compact>
                    <Indicator isNumbered={isNumbered} />
                    <div>
                      <ListHeading>{li.heading}</ListHeading>
                      {li.body && li.body}
                    </div>
                  </ListItem>
                ))}
              </List>
            ))}
        </ListWrapper>
      </ColumnOne>
      <ColumnTwo margin={imageMargin}>
        <Image src={image} alt="image describing product features" />
      </ColumnTwo>
    </InnerWrapper>
    {showDisclaimer && (
      <Disclaimer>
        The product images shown are for illustration purposes only and may not
        be an exact representation of the product.
      </Disclaimer>
    )}
  </Wrapper>
);

ImageCard.propTypes = {
  heading: PropTypes.string.isRequired,
  highlight: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  imageMargin: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      body: PropTypes.string,
      heading: PropTypes.string.isRequired
    })
  ),
  paragraphs: PropTypes.array.isRequired,
  showDisclaimer: PropTypes.bool,
  isNumbered: PropTypes.bool
};

export default ImageCard;
